/** CMS-editable Privacy Policy config for /privacy-policy (Super Admin + public page). */

export const PRIVACY_SECTION_ICON_OPTIONS = [
  { value: "user", label: "User" },
  { value: "database", label: "Database" },
  { value: "cog", label: "Settings / Cog" },
  { value: "share", label: "Share" },
  { value: "shield", label: "Shield" },
  { value: "key", label: "Key / Rights" },
  { value: "cookie", label: "Cookie" },
  { value: "globe", label: "Globe / Links" },
  { value: "archive", label: "Archive" },
  { value: "child", label: "Children" },
  { value: "sync", label: "Sync / Changes" },
  { value: "envelope", label: "Envelope / Contact" },
];

export const PRIVACY_HERO_INTRO =
  "Your privacy is important to us. This policy explains how Trocanet collects, uses, protects, and discloses your information.";

export const PRIVACY_CTA = {
  title: "Your privacy & trust are our top priorities",
  subtitle:
    "We are committed to protecting your information and being transparent about how we use it.",
};

export const DEFAULT_PRIVACY_SECTIONS = [
  {
    id: "introduction",
    title: "Introduction",
    summary:
      'Trocanet ("we", "our", or "us") values your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our B2B marketplace platform, website, and services.',
    body: `<p>Trocanet ("we", "our", or "us") values your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our B2B marketplace platform, website, and services.</p>
<p>By accessing or using Trocanet, you agree to the practices described in this policy. If you do not agree, please discontinue use of our services.</p>`,
    icon: "user",
  },
  {
    id: "information-we-collect",
    title: "Information We Collect",
    summary:
      "We collect information you provide directly to us and automatically when you use our platform. This may include personal information, business information, usage data, and cookies.",
    body: `<p>We collect information you provide directly to us and automatically when you use our platform. This may include:</p>
<ul>
<li><strong>Personal information</strong> — name, email address, phone number, and login credentials</li>
<li><strong>Business information</strong> — company name, tax IDs, address, industry, and product or service details</li>
<li><strong>Usage data</strong> — pages viewed, search queries, feature usage, and transaction activity</li>
<li><strong>Cookies &amp; device data</strong> — browser type, IP address, device identifiers, and similar technologies</li>
</ul>`,
    icon: "database",
  },
  {
    id: "how-we-use",
    title: "How We Use Your Information",
    summary:
      "We use the information we collect to provide, maintain, and improve our services, process transactions, communicate with you, ensure security, and comply with legal obligations.",
    body: `<p>We use the information we collect to:</p>
<ul>
<li>Provide, maintain, and improve our marketplace services</li>
<li>Process transactions, RFQs, orders, and related workflows</li>
<li>Communicate with you about your account, updates, and support requests</li>
<li>Ensure platform security and prevent fraud or abuse</li>
<li>Comply with legal, tax, and regulatory obligations</li>
</ul>`,
    icon: "cog",
  },
  {
    id: "how-we-share",
    title: "How We Share Your Information",
    summary:
      "We do not sell your personal information. We may share your data with service providers, business partners, legal authorities, or in case of business transfers.",
    body: `<p>We do not sell your personal information. We may share your data with:</p>
<ul>
<li><strong>Service providers</strong> — hosting, payments, analytics, messaging, and KYC vendors who support our operations</li>
<li><strong>Business partners</strong> — buyers, sellers, manufacturers, logistics partners, and others you engage with on the platform</li>
<li><strong>Legal authorities</strong> — when required by law or to protect rights, safety, and security</li>
<li><strong>Business transfers</strong> — in connection with a merger, acquisition, or similar corporate event</li>
</ul>`,
    icon: "share",
  },
  {
    id: "data-security",
    title: "Data Security",
    summary:
      "We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction.",
    body: `<p>We implement industry-standard security measures to protect your information from unauthorized access, alteration, disclosure, or destruction. These may include encryption in transit, access controls, monitoring, and staff confidentiality obligations.</p>
<p>No method of transmission or storage is completely secure. We continuously work to improve our protections, but cannot guarantee absolute security.</p>`,
    icon: "shield",
  },
  {
    id: "your-rights",
    title: "Your Rights & Choices",
    summary:
      "You have the right to access, update, correct, or delete your personal data. You can also opt out of marketing communications at any time.",
    body: `<p>Subject to applicable law, you have the right to:</p>
<ul>
<li>Access, update, correct, or delete your personal data</li>
<li>Opt out of marketing communications at any time</li>
<li>Request restriction of certain processing where applicable</li>
</ul>
<p>You can update many profile fields in your account settings. For other requests, contact us using the details in the Contact Us section.</p>`,
    icon: "key",
  },
  {
    id: "cookies",
    title: "Cookies & Tracking Technologies",
    summary:
      "We use cookies and similar technologies to enhance your experience, analyze platform usage, and deliver relevant content and ads.",
    body: `<p>We use cookies and similar technologies to enhance your experience, analyze platform usage, and deliver relevant content and ads. You can control cookies through your browser settings. Disabling certain cookies may limit some marketplace features.</p>
<p>For more detail, please also review our Cookie Policy.</p>`,
    icon: "cookie",
  },
  {
    id: "third-party-links",
    title: "Third-Party Links",
    summary:
      "Our platform may contain links to third-party websites. We are not responsible for their content or privacy practices.",
    body: `<p>Our platform may contain links to third-party websites or services. We are not responsible for their content, security, or privacy practices. We encourage you to review the privacy policies of any third-party sites you visit.</p>`,
    icon: "globe",
  },
  {
    id: "data-retention",
    title: "Data Retention",
    summary:
      "We retain your information only as long as necessary for the purposes outlined in this policy or as required by law.",
    body: `<p>We retain your information only as long as necessary for the purposes outlined in this policy or as required by law. Retention periods may vary based on account activity, transaction history, and legal obligations. When data is no longer required, we delete or anonymize it where practicable.</p>`,
    icon: "archive",
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    summary:
      "Our platform is not intended for children under 18. We do not knowingly collect personal information from children.",
    body: `<p>Our platform is not intended for children under 18. We do not knowingly collect personal information from children. If you believe a minor has provided us with personal data, please contact us and we will take steps to delete it where appropriate.</p>`,
    icon: "child",
  },
  {
    id: "changes",
    title: "Changes to This Policy",
    summary:
      'We may update this Privacy Policy from time to time. We will notify you about significant changes by updating the "Last Updated" date.',
    body: `<p>We may update this Privacy Policy from time to time. We will notify you about significant changes by updating the "Last Updated" date on this page. Where required, we may also provide additional notice on the platform or by email.</p>
<p>Continued use of Trocanet after an update constitutes acceptance of the revised policy, except where additional consent is required by law.</p>`,
    icon: "sync",
  },
  {
    id: "contact-us",
    title: "Contact Us",
    summary:
      "If you have any questions about this Privacy Policy, please contact us using the details below.",
    body: `<p>If you have any questions about this Privacy Policy, please contact us:</p>
<ul>
<li><strong>Email:</strong> <a href="mailto:support@trocanet.com">support@trocanet.com</a></li>
<li><strong>Contact form:</strong> <a href="/contact">trocanet.com/contact</a></li>
</ul>
<p>We aim to respond within a reasonable period consistent with applicable law.</p>`,
    icon: "envelope",
  },
];

export const DEFAULT_PRIVACY_CMS_CONFIG = {
  schemaVersion: 2,
  heroBackgroundUrl: "",
  intro: PRIVACY_HERO_INTRO,
  sections: DEFAULT_PRIVACY_SECTIONS.map((s) => ({ ...s, visible: true })),
};

function isPlainObject(value) {
  return value != null && typeof value === "object" && !Array.isArray(value);
}

function slugify(value, fallback) {
  const slug = String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
  return slug || fallback;
}

/** Merge CMS section edits onto the fixed 12 platform section slots. */
function mergeSections(defaults, incoming) {
  const byId = new Map();
  if (Array.isArray(incoming)) {
    for (const item of incoming) {
      if (!isPlainObject(item)) continue;
      const id = slugify(item.id || item.title, "");
      if (id) byId.set(id, item);
    }
  }
  const allowedIcons = new Set(PRIVACY_SECTION_ICON_OPTIONS.map((o) => o.value));
  return defaults.map((base) => {
    const row = byId.get(base.id) || {};
    const iconValue = String(row.icon ?? base.icon ?? "shield").trim();
    return {
      id: base.id,
      title: String(row.title ?? base.title ?? "").trim() || base.title,
      summary: String(row.summary ?? base.summary ?? "").trim() || base.summary,
      body: String(row.body ?? base.body ?? "").trim() || base.body,
      icon: allowedIcons.has(iconValue) ? iconValue : base.icon || "shield",
      visible: row.visible !== false,
    };
  });
}

export function parsePrivacyCmsConfig(content) {
  if (!content || typeof content !== "string") {
    return {
      ...DEFAULT_PRIVACY_CMS_CONFIG,
      sections: DEFAULT_PRIVACY_CMS_CONFIG.sections.map((s) => ({ ...s })),
    };
  }

  const trimmed = content.trim();
  if (!trimmed.startsWith("{")) {
    return {
      ...DEFAULT_PRIVACY_CMS_CONFIG,
      sections: DEFAULT_PRIVACY_CMS_CONFIG.sections.map((s) => ({ ...s })),
    };
  }

  try {
    const parsed = JSON.parse(trimmed);
    if (!isPlainObject(parsed)) {
      return {
        ...DEFAULT_PRIVACY_CMS_CONFIG,
        sections: DEFAULT_PRIVACY_CMS_CONFIG.sections.map((s) => ({ ...s })),
      };
    }

    const defaults = DEFAULT_PRIVACY_CMS_CONFIG;
    return {
      schemaVersion: 2,
      heroBackgroundUrl: String(parsed.heroBackgroundUrl ?? defaults.heroBackgroundUrl).trim(),
      intro: defaults.intro,
      sections: mergeSections(defaults.sections, parsed.sections),
    };
  } catch {
    return {
      ...DEFAULT_PRIVACY_CMS_CONFIG,
      sections: DEFAULT_PRIVACY_CMS_CONFIG.sections.map((s) => ({ ...s })),
    };
  }
}

export function serializePrivacyCmsConfig(config) {
  const defaults = DEFAULT_PRIVACY_CMS_CONFIG;
  const source = isPlainObject(config) ? config : defaults;
  return JSON.stringify(
    {
      schemaVersion: 2,
      heroBackgroundUrl: String(source.heroBackgroundUrl ?? defaults.heroBackgroundUrl).trim(),
      intro: defaults.intro,
      sections: mergeSections(defaults.sections, source.sections),
    },
    null,
    2,
  );
}

export function privacyConfigFromCmsRecord(record) {
  return parsePrivacyCmsConfig(record?.content);
}

export function privacyCmsPayloadFromConfig(config) {
  const content = serializePrivacyCmsConfig(config);
  const data = parsePrivacyCmsConfig(content);
  return {
    title: "Privacy Policy",
    description: (data.intro || "").slice(0, 200),
    content,
  };
}

/** Public page model from CMS record (JSON preferred; legacy HTML ignored → defaults). */
export function buildPrivacyPageModel(page) {
  const config = parsePrivacyCmsConfig(page?.content);
  const sections = (config.sections?.length ? config.sections : DEFAULT_PRIVACY_SECTIONS).filter(
    (section) => section.visible !== false,
  );
  return {
    title: "Privacy Policy",
    intro: config.intro || PRIVACY_HERO_INTRO,
    heroBackgroundUrl: config.heroBackgroundUrl || "",
    effectiveDate: page?.effectiveDate || null,
    updatedAt: page?.updatedAt || null,
    cta: {
      title: PRIVACY_CTA.title,
      subtitle: PRIVACY_CTA.subtitle,
    },
    sections,
    fromCms: Boolean(page?.content && String(page.content).trim().startsWith("{")),
  };
}
